import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { X, Paperclip, RefreshCw, Trash2, RotateCcw } from "lucide-react";
import { toast } from "react-toastify";
import DeliveryNoteViewModal from "./DeliveryNoteViewModal";
import { adminServices } from "../services/AdminServices";

const formatDate = (dateString) => {
  if (!dateString) return "";
  const d = new Date(dateString);
  if (isNaN(d)) return dateString;
  return d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
};

const normalizeItem = (item) => {
  if (!item || typeof item !== "object") return {};
  return {
    ...item,
    productId: item.productId ?? item.product_id ?? item.productID,
    sku: item.sku ?? item.productSku ?? item.product_sku ?? item.SKU,
    description: item.description ?? item.productName ?? item.product_name ?? item.name ?? item.product ?? item.itemName,
    uomName: item.uomName ?? item.uom_name ?? item.UomName ?? item.uom ?? item.unitOfMeasure ?? item.unit,
    quantity: item.quantity ?? item.qty ?? item.poQty ?? item.po_quantity ?? item.orderQty,
    unitPrice: item.unitPrice ?? item.price ?? item.cost ?? item.unit_price,
  };
};

const EditDeliveryNoteModal = ({ isOpen, onClose, onSaveDraft, onSubmit, selectedPO, deliveryNoteId, orderDetails }) => {
  const [deliveryRows, setDeliveryRows] = useState([]);
  const [processedBy, setProcessedBy] = useState("");
  const [overallRemarks, setOverallRemarks] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showFinalizeModal, setShowFinalizeModal] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [snapshot, setSnapshot] = useState(null);
  const [merchantDetails, setMerchantDetails] = useState(null);
  const fileInputRef = useRef(null);

  const order = orderDetails?.order ?? {};
  const managerApproval = orderDetails?.managerApprovals?.[0] ?? {};
  const rawItems = selectedPO?.items ?? orderDetails?.purchaseOrders?.[0]?.items ?? managerApproval?.items ?? [];
  const poItems = rawItems.length > 0 ? rawItems.map(normalizeItem) : [];
  // eslint-disable-next-line no-console
  console.log("[EditDN]", JSON.stringify({ selectedPO, rawItem: rawItems[0], poItem: poItems[0], order }, null, 2));
  const poLabel = selectedPO?.filename
    ? selectedPO.filename.replace(/\.pdf$/i, "")
    : "PO";

  const isDirty =
    deliveryRows.some((r) => r.deliveredQty !== "" || (r.remarks && r.remarks !== "-")) ||
    processedBy.trim() !== "" ||
    overallRemarks.trim() !== "" ||
    attachment !== null;

  useEffect(() => {
    if (deliveryNoteId && selectedPO) {
      setDeliveryRows(poItems.map((item) => ({
        deliveredQty: item.deliveredQty ?? item.quantity ?? "",
        remarks: item.remarks || item.item_remarks || "-",
      })));
      setProcessedBy(selectedPO.inspectedBy || selectedPO.notedBy || "");
      setOverallRemarks(selectedPO.remarks || "");
    } else {
      setDeliveryRows(poItems.map(() => ({ deliveredQty: "", remarks: "-" })));
      setProcessedBy("");
      setOverallRemarks("");
    }
    setAttachment(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryNoteId, orderDetails, selectedPO]);

  useEffect(() => {
    if (!order.merchantId) {
      setMerchantDetails(null);
      return;
    }
    let cancelled = false;
    adminServices.getMerchantById(order.merchantId)
      .then((res) => {
        // eslint-disable-next-line no-console
        console.log("[EditDN] getMerchantById response:", res);
        const data = res?.data?.data ?? res?.data;
        if (!cancelled) setMerchantDetails(data || null);
      })
      .catch(() => {
        if (!cancelled) setMerchantDetails(null);
      });
    return () => { cancelled = true; };
  }, [order.merchantId]);

  if (!isOpen) return null;

  const handleRowChange = (idx, field, value) => {
    setDeliveryRows((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const handleResetRow = (idx) => {
    setDeliveryRows((prev) => {
      const next = [...prev];
      next[idx] = { deliveredQty: "", remarks: "-" };
      return next;
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) setAttachment(file);
    e.target.value = "";
  };

  const handleRemoveAttachment = () => setAttachment(null);

  const buildPayload = () => {
    const purchaseOrderId = selectedPO?.purchase_order_id ?? selectedPO?.purchaseOrderId ?? selectedPO?.id;
    return {
      purchaseOrderId,
      purchase_order_id: purchaseOrderId,
      processedBy,
      remarks: overallRemarks,
      items: poItems.map((item, idx) => ({
        ...item,
        productId: item.productId ?? item.product_id,
        deliveredQty: Number(deliveryRows[idx]?.deliveredQty) || 0,
        remarks: deliveryRows[idx]?.remarks || "",
      })),
    };
  };

  const handleSaveDraft = async () => {
    const payload = buildPayload();
    if (!payload.purchaseOrderId) {
      toast.error("Purchase Order ID is missing");
      return;
    }
    setSaving(true);
    try {
      await onSaveDraft?.(payload, attachment);
      onClose?.();
    } catch {
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    const payload = buildPayload();
    if (!payload.purchaseOrderId) {
      toast.error("Purchase Order ID is missing");
      return;
    }
    setShowFinalizeModal(false);
    setSaving(true);
    try {
      await onSubmit?.(payload, attachment);
      setSnapshot({
        rows: deliveryRows.map((r) => ({ ...r })),
        processedBy,
        remarks: overallRemarks,
        attachment,
      });
      setShowViewModal(true);
    } catch {
    } finally {
      setSaving(false);
    }
  };

  const handleCloseClick = () => {
    if (isDirty) {
      setShowUnsavedModal(true);
    } else {
      onClose?.();
    }
  };

  if (showViewModal) {
    return (
      <DeliveryNoteViewModal
        isOpen
        onClose={() => { setShowViewModal(false); onClose?.(); }}
        poItems={poItems}
        poLabel={poLabel}
        order={order}
        managerApproval={managerApproval}
        snapshot={snapshot}
      />
    );
  }

  return ReactDOM.createPortal(
    <div className="edn-modal-overlay" onClick={handleCloseClick}>

      {showFinalizeModal && (
        <div
          className="edn-confirm-overlay"
          onClick={(e) => { e.stopPropagation(); setShowFinalizeModal(false); }}
        >
          <div className="edn-confirm-box" onClick={(e) => e.stopPropagation()}>
            <h3 className="edn-confirm-title">Finalize Delivery Note</h3>
            <p className="edn-confirm-message">
              Are you sure you want to finalize this Delivery Note? Once finalized, it can no longer be edited.
            </p>
            <button
              className="edn-confirm-btn edn-confirm-btn--primary"
              onClick={handleSubmit}
              disabled={saving}
              type="button"
            >
              {saving ? "Submitting…" : "Proceed"}
            </button>
            <button
              className="edn-confirm-btn edn-confirm-btn--ghost"
              onClick={() => setShowFinalizeModal(false)}
              type="button"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {showUnsavedModal && (
        <div
          className="edn-confirm-overlay"
          onClick={(e) => { e.stopPropagation(); setShowUnsavedModal(false); }}
        >
          <div className="edn-confirm-box" onClick={(e) => e.stopPropagation()}>
            <h3 className="edn-confirm-title">Unsaved Changes</h3>
            <p className="edn-confirm-message">
              You have unsaved changes. If you leave now, your changes will be lost.
            </p>
            <button
              className="edn-confirm-btn edn-confirm-btn--danger"
              onClick={() => { setShowUnsavedModal(false); onClose?.(); }}
              type="button"
            >
              Leave
            </button>
            <button
              className="edn-confirm-btn edn-confirm-btn--ghost"
              onClick={() => setShowUnsavedModal(false)}
              type="button"
            >
              Go Back
            </button>
          </div>
        </div>
      )}

      <div className="edn-modal-container" onClick={(e) => e.stopPropagation()}>

        <div className="edn-modal-header">
          <span className="edn-modal-header-title">Edit Delivery Note</span>
          <button className="edn-modal-close-btn" onClick={handleCloseClick} aria-label="Close" type="button">
            <X size={20} />
          </button>
        </div>

        <div className="edn-modal-body">

          <div className="edn-doc-preview">
            <p className="edn-doc-title">DELIVERY NOTE</p>

            <div className="edn-doc-meta">
              <div className="edn-doc-meta-row">
                <div className="edn-doc-meta-cell">
                  <span className="edn-doc-meta-label">Order Type:</span>
                  <span className="edn-doc-meta-value">{order.orderType ?? "Regular Order"}</span>
                </div>
                <div className="edn-doc-meta-cell">
                  <span className="edn-doc-meta-label">Date Issued:</span>
                  <span className="edn-doc-meta-value">{formatDate(new Date().toISOString())}</span>
                </div>
              </div>
              <div className="edn-doc-meta-row">
                <div className="edn-doc-meta-cell">
                  <span className="edn-doc-meta-label">Merchant Name:</span>
                  <span className="edn-doc-meta-value">
                    {merchantDetails?.merchant_name ??
                      merchantDetails?.merchantName ??
                      merchantDetails?.name ??
                      order.merchant?.merchantName ??
                      order.merchant?.merchant_name ??
                      order.merchant?.name ??
                      order.merchantName ??
                      order.merchant_name ??
                      order.merchant?.id ??
                      order.merchantId ??
                      "-"}
                  </span>
                </div>
                <div className="edn-doc-meta-cell">
                  <span className="edn-doc-meta-label">Store Code:</span>
                  <span className="edn-doc-meta-value">{merchantDetails?.merchantCode ?? order.merchant?.merchantCode ?? order.facility?.code ?? order.facilityCode ?? order.merchant?.id ?? order.merchantId ?? "-"}</span>
                </div>
              </div>
              <div className="edn-doc-meta-row">
                <div className="edn-doc-meta-cell">
                  <span className="edn-doc-meta-label">Location:</span>
                  <span className="edn-doc-meta-value">{merchantDetails?.location ?? order.facility?.name ?? order.facilityName ?? order.colony?.colonyName ?? "-"}</span>
                </div>
                <div className="edn-doc-meta-cell">
                  <span className="edn-doc-meta-label">PR No.:</span>
                  <span className="edn-doc-meta-value">{managerApproval.prNo ?? "-"}</span>
                </div>
              </div>
            </div>

            {poItems.length === 0 && (
              <div className="process-document-placeholder" style={{ marginBottom: 16 }}>
                No items found for this Purchase Order.
              </div>
            )}
            <div className="edn-doc-table-wrap">
              <table className="edn-doc-table">
                <thead>
                  <tr className="edn-doc-table-col-header">
                    <th>No.</th>
                    <th>SKU</th>
                    <th>Item Description</th>
                    <th>UOM</th>
                    <th>PO QTY</th>
                    <th>Delivered QTY</th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const MIN_ROWS = 10;
                    const dataRows = poItems.map((item, idx) => {
                      const hasRemark = deliveryRows[idx]?.remarks && deliveryRows[idx].remarks !== "-";
                      return (
                        <tr
                          key={idx}
                          className={[
                            "edn-doc-table-row",
                            idx % 2 !== 0 ? "edn-doc-table-row--alt" : "",
                            hasRemark ? "edn-doc-table-row-flagged" : "",
                          ].join(" ").trim()}
                        >
                          <td className={hasRemark ? "edn-doc-table-no-flagged" : ""}>{idx + 1}</td>
                          <td className={hasRemark ? "edn-doc-table-sku-flagged" : ""}>{item.sku ?? item.productSku ?? "-"}</td>
                          <td>{item.description ?? item.productName ?? item.name ?? "-"}</td>
                          <td>{item.uomName ?? item.UomName ?? item.uom ?? "-"}</td>
                          <td>{item.quantity ?? item.qty ?? "-"}</td>
                          <td>
                            <input
                              type="number"
                              className="edn-doc-table-qty-input"
                              value={deliveryRows[idx]?.deliveredQty ?? ""}
                              min={0}
                              onChange={(e) => handleRowChange(idx, "deliveredQty", e.target.value)}
                            />
                          </td>
                          <td>
                            <div className="edn-doc-table-remarks-cell">
                              <input
                                type="text"
                                className="edn-doc-table-remarks-input"
                                value={deliveryRows[idx]?.remarks ?? "-"}
                                onChange={(e) => handleRowChange(idx, "remarks", e.target.value)}
                              />
                              {(deliveryRows[idx]?.deliveredQty || hasRemark) && (
                                <button
                                  className="edn-doc-table-reset-btn"
                                  onClick={() => handleResetRow(idx)}
                                  title="Reset row"
                                  type="button"
                                >
                                  <RotateCcw size={14} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    });
                    const emptyCount = Math.max(0, MIN_ROWS - poItems.length);
                    const emptyRows = Array.from({ length: emptyCount }, (_, i) => (
                      <tr key={`empty-${i}`} className={(poItems.length + i) % 2 !== 0 ? "edn-doc-table-row--alt" : "edn-doc-table-row"}>
                        <td>&nbsp;</td><td></td><td></td><td></td><td></td><td></td><td></td>
                      </tr>
                    ));
                    return [...dataRows, ...emptyRows];
                  })()}
                </tbody>
              </table>
            </div>

            <div className="edn-doc-sig-grid">
              <div className="edn-doc-sig-cell">
                <span className="edn-doc-sig-label">Processed by</span>
                <span className="edn-doc-sig-value">Employee Name Here</span>
              </div>
              <div className="edn-doc-sig-cell">
                <span className="edn-doc-sig-label">Noted by</span>
                <span className="edn-doc-sig-value">General Manager Name Here</span>
              </div>
              <div className="edn-doc-sig-cell">
                <span className="edn-doc-sig-label">Date Inspected</span>
                <span className="edn-doc-sig-value"></span>
              </div>
              <div className="edn-doc-sig-cell">
                <span className="edn-doc-sig-label">Inspected by</span>
                <span className="edn-doc-sig-value"></span>
              </div>
              <div className="edn-doc-sig-cell">
                <span className="edn-doc-sig-label">Date Received</span>
                <span className="edn-doc-sig-value"></span>
              </div>
              <div className="edn-doc-sig-cell">
                <span className="edn-doc-sig-label">Received by (PDL Merchant)</span>
                <span className="edn-doc-sig-value"></span>
              </div>
            </div>

            <p className="edn-doc-page-footer">PAGE 1 OUT OF X</p>
          </div>

          <div className="edn-form edn-form--spaced">
            <div className="edn-form-row">
              <div className="edn-form-group">
                <label className="edn-form-label">Attachment</label>
                {attachment ? (
                  <div className="edn-attachment-chip">
                    <Paperclip size={14} />
                    <span className="edn-attachment-name" title={attachment.name}>
                      {attachment.name.length > 22 ? attachment.name.slice(0, 19) + "..." : attachment.name}
                    </span>
                    <span className="edn-attachment-full">{attachment.name}</span>
                    <button
                      className="edn-attachment-action"
                      onClick={() => fileInputRef.current?.click()}
                      title="Replace file"
                      type="button"
                    >
                      <RefreshCw size={14} />
                    </button>
                    <button
                      className="edn-attachment-action edn-attachment-delete"
                      onClick={handleRemoveAttachment}
                      title="Remove"
                      type="button"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ) : (
                  <button
                    className="edn-attachment-empty"
                    onClick={() => fileInputRef.current?.click()}
                    type="button"
                  >
                    <Paperclip size={14} />
                    Attach file
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                  accept=".pdf,.jpg,.jpeg,.png"
                />
              </div>

              <div className="edn-form-group">
                <label className="edn-form-label">Processed by</label>
                <input
                  type="text"
                  className="edn-form-input"
                  placeholder="Employee Name"
                  value={processedBy}
                  onChange={(e) => setProcessedBy(e.target.value)}
                />
              </div>
            </div>

            <div className="edn-form-group">
              <label className="edn-form-label">Remarks</label>
              <textarea
                className="edn-form-textarea"
                rows={3}
                value={overallRemarks}
                onChange={(e) => setOverallRemarks(e.target.value)}
              />
            </div>
          </div>

          <div className="edn-page-footer">
            <button
              className="edn-btn-draft"
              onClick={handleSaveDraft}
              disabled={saving}
              type="button"
            >
              Save as Draft
            </button>
            <button
              className="edn-btn-submit"
              onClick={() => setShowFinalizeModal(true)}
              disabled={saving}
              type="button"
            >
              Submit
            </button>
          </div>

        </div>

      </div>

    </div>,
    document.body
  );
};

export default EditDeliveryNoteModal;
